import React from "react";
import { Camera, Video, PenTool, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format, isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { getPlatformStyle } from "../constants/platformIcons";
import { getStatusStyle } from "../constants/statusConstants";
import { getBaseUrl } from "../../api/api";
import { PLATFORM_IMAGE } from "../constants/platform";

interface PostCardProps {
  title: string;
  scheduledTimeUTC?: number | string;
  timezone?: string;
  status?: string;
  platform?: string;
  postType?: string;
  onDelete?: () => void;
}

const PostCard: React.FC<PostCardProps> = ({
  title,
  scheduledTimeUTC,
  timezone,
  status,
  platform,
  postType,
  onDelete,
}) => {
  const { t } = useTranslation();
  const style = getPlatformStyle(platform || "");
  const statusUpper = String(status || "DRAFT").toUpperCase();
  const statusStyle = getStatusStyle(statusUpper);
  const statusLabel = status ? t(statusStyle.labelKey) : t("Unscheduled");
  const canDelete = Boolean(onDelete) && (statusUpper === "DRAFT" || statusUpper === "CANCELLED");

  const formatTime = (scheduledTimeValue: number | string | undefined): string => {
    if (!scheduledTimeValue) return "09:15 AM";

    try {
      const date =
        typeof scheduledTimeValue === "string"
          ? scheduledTimeValue.includes("T") || scheduledTimeValue.includes("-")
            ? parseISO(scheduledTimeValue)
            : (() => {
                const timestamp = parseInt(scheduledTimeValue, 10);
                return isNaN(timestamp) || timestamp <= 0 ? null : new Date(timestamp);
              })()
          : scheduledTimeValue > 0
            ? new Date(scheduledTimeValue)
            : null;

      if (!date || !isValid(date)) return "09:15 AM";

      const scheduledDateTime = new Date(
        formatInTimeZone(
          date,
          timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd HH:mm:ss"
        )
      );
      return format(scheduledDateTime, "hh:mm aa");
    } catch {
      return "09:15 AM";
    }
  };

  const formattedTime = formatTime(scheduledTimeUTC);

  const MediaIcon = (() => {
    switch (String(postType ?? "").toLowerCase()) {
      case "image":
        return <Camera className="w-[18px] h-[18px] text-gray-700" strokeWidth={1.5} />;
      case "video":
        return <Video className="w-[18px] h-[18px] text-gray-700" strokeWidth={1.5} />;
      case "text":
        return <PenTool className="w-[18px] h-[18px] text-gray-700" strokeWidth={1.5} />;
      default:
        return <Camera className="w-[18px] h-[18px] text-gray-700" strokeWidth={1.5} />;
    }
  })();

  return (
    <div
      className={`group relative mb-3 rounded-lg border ${style.border} ${style.bg} p-2 sm:p-3 pb-6`}
    >
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center">
          <img
            src={`${getBaseUrl()}${PLATFORM_IMAGE[platform]}`}
            className="h-6 w-6"
            alt={platform}
          />
        </div>
        {formattedTime && (
          <span className="text-xs sm:text-[14px] leading-4 sm:leading-5 text-gray-500 font-medium flex-shrink-0">
            {formattedTime}
          </span>
        )}
      </div>
      <div
        className="mb-3 sm:mb-4 text-xs sm:text-[14px] font-medium leading-4 sm:leading-5 text-gray-900 overflow-hidden"
        title={title}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" as any,
          wordBreak: "break-word",
          textOverflow: "ellipsis",
        }}
        aria-label={title}
      >
        {title}
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 sm:w-[18px] sm:h-[18px] flex-shrink-0">{MediaIcon}</div>
          <div
            className={`${statusStyle.bgClass} flex items-center gap-1.5 rounded-full px-2 py-1`}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${statusStyle.dotClass}`} />
            <span className={`text-[9px] sm:text-[10px] font-medium ${statusStyle.textClass}`}>
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
      {canDelete && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDelete?.();
          }}
          className="absolute right-2 bottom-3 inline-flex items-center justify-center gap-1 rounded-full bg-red-500 px-3 py-1 text-[10px] font-medium text-white opacity-0 shadow-md transition-opacity duration-150 delay-0 group-hover:opacity-100 group-hover:delay-[500ms]"
          style={{ minWidth: 54 }}
        >
          <Trash2 className="h-[10px] w-[10px]" />
          {t("Delete")}
        </button>
      )}
    </div>
  );
};

export default PostCard;
